import { useEffect, useState } from "react";
import { Calculator, ChevronDown, ChevronUp, Tag, X } from "lucide-react";
import Decimal from "decimal.js";
import { formatMoney, toDecimal } from "../../lib/money";
import { toSpecialPricePayload, type DraftTotals } from "./useQuoteDraft";
import type { TaxMode } from "../../api/types";

// 釘在右下的即時總計。這裡的數字只是編輯時的預覽,
// 存檔時後端一律重算並覆寫,所以下方明講「以後端為準」。
//
// 版面上的規矩:position 用 sticky 而不是 fixed,而且這一塊排在編輯器的最後。
// sticky 會在版面裡佔自己的位置 —— 捲到最下面時它就停在所有內容的下方,
// 不會壓在任何一列的金額上;fixed 則會永遠浮在畫面上,最後一列一定被蓋住。
// 捲動途中還是會浮在內容之上,所以另外給一顆收合鈕:縮成一行之後,
// 連小螢幕(表格要橫向捲才看得到單價 / 金額)也不會被擋。

// 稅制只剩兩種。業主 2026-09-11:「根本沒有免稅這詞」,所以這裡也沒有第三個標籤。
// 含稅的稅額一律是 0(總額就是小計),標籤照樣寫出來,讓人看得出那個 0 是哪來的。
const TAX_LABEL: Record<TaxMode, string> = {
  EXCLUSIVE: "營業稅(外加)",
  INCLUSIVE: "營業稅(內含)",
};

/** 收合與否記在瀏覽器,換一張單、換一頁都還是使用者上次選的樣子。 */
const STORAGE_KEY = "nq-totals-collapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // 隱私模式或關掉儲存空間時讀不到,一律當作展開。
    return false;
  }
}

export interface TotalsBarProps {
  totals: DraftTotals;
  taxMode: TaxMode;
  /**
   * 設定或取消一口價(業主 2026-10-01)。傳 null 是取消。
   * 沒傳就是唯讀:照樣顯示優惠價,但沒有按鈕。
   */
  onSpecialPriceChange?: (value: string | null) => void;
}

export function TotalsBar({ totals, taxMode, onSpecialPriceChange }: TotalsBarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  // 一口價的輸入框只在按了「一口價」之後出現 —— 平常不佔即時總計的版面。
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState("");

  const special = totals.special_price ?? null;
  const editable = onSpecialPriceChange !== undefined;

  function apply() {
    const value = toSpecialPricePayload(typed);
    if (value === null) return;  // 空的、0、負數都不算一口價
    onSpecialPriceChange?.(value);
    setEditing(false);
  }

  function startEditing() {
    setTyped(special === null ? "" : new Decimal(special).toFixed(0));
    setEditing(true);
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* 存不進去不影響本次操作 */
    }
  }, [collapsed]);

  // 有一口價時(業主 2026-10-01 選的版面):原價照列 —— 小計、原本的稅 ——
  // 原本的總計畫刪除線,下面一列紅字「優惠價(含稅)」,未稅單再一行小字拆出
  // 未稅與稅額。一口價就是總計,稅是從它倒推的。
  const rows: Array<{ label: string; value: string; strong?: boolean; struck?: boolean; offer?: boolean }> =
    special === null
      ? [
          { label: "小計", value: formatMoney(totals.subtotal) },
          { label: TAX_LABEL[taxMode], value: formatMoney(totals.tax_amount) },
          { label: "總計", value: formatMoney(totals.total), strong: true },
        ]
      : [
          { label: "小計", value: formatMoney(totals.subtotal) },
          ...(taxMode === "EXCLUSIVE"
            ? [{ label: TAX_LABEL[taxMode], value: formatMoney(totals.list_tax_amount ?? "0") }]
            : []),
          { label: "總計", value: formatMoney(totals.list_total ?? totals.subtotal), struck: true },
          { label: "優惠價(含稅)", value: formatMoney(totals.total), strong: true, offer: true },
        ];
  const breakdown =
    special !== null && taxMode === "EXCLUSIVE"
      ? `未稅 ${formatMoney(totals.net_amount ?? "0")} + 營業稅 ${formatMoney(totals.tax_amount)}`
      : null;
  // 改了明細之後原價可能掉到優惠價以下 —— 那就不是優惠了。提醒,但不擋存檔。
  const notADiscount =
    special !== null && toDecimal(special).greaterThanOrEqualTo(toDecimal(totals.list_total ?? totals.subtotal));

  return (
    <section
      aria-label="即時總計"
      className={
        // `nq-clear-assistant`:右下角那一塊是助理吉祥物的,讓開它。
        // 業主 2026-09-17:「報價單的 即時總計 畫面被 AI星星 logo擋住了」。
        "nq-clear-assistant sticky bottom-4 z-30 ml-auto w-[min(20rem,100%)] " +
        "rounded-[var(--radius)] border " +
        "border-[var(--border-strong)] bg-[var(--surface)] px-3 py-2.5 shadow-card"
      }
    >
      <div className="flex items-center justify-between gap-2">
        <p className="m-0 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--text-3)]">
          <Calculator aria-hidden="true" size={14} />
          即時總計
        </p>
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={collapsed ? "展開即時總計" : "收合即時總計"}
          aria-expanded={!collapsed}
          className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
        >
          {collapsed ? <ChevronUp aria-hidden="true" size={16} /> : <ChevronDown aria-hidden="true" size={16} />}
        </button>
      </div>

      {collapsed ? (
        // 收起來也要看得到總計,不然等於把功能關掉。
        <p className="m-0 mt-0.5 flex items-baseline justify-between gap-3">
          <span className="text-sm text-[var(--text-2)]">總計</span>
          <span className="tabular text-lg font-semibold text-[var(--text)]">{formatMoney(totals.total)}</span>
        </p>
      ) : (
        <>
          <dl className="m-0 mt-1.5 flex flex-col gap-1">
            {rows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <dt className={`m-0 text-sm ${row.strong ? "font-medium text-[var(--text)]" : "text-[var(--text-2)]"}`}>
                  {row.label}
                </dt>
                <dd
                  className={
                    "tabular m-0 " +
                    (row.offer
                      ? "text-lg font-semibold text-[var(--danger)]"
                      : row.strong
                        ? "text-lg font-semibold text-[var(--text)]"
                        : "text-sm text-[var(--text)]")
                  }
                >
                  {row.struck ? <s className="text-[var(--text-3)]">{row.value}</s> : row.value}
                </dd>
              </div>
            ))}
          </dl>
          {breakdown ? <p className="tabular m-0 text-right text-xs text-[var(--text-2)]">{breakdown}</p> : null}
          {notADiscount ? (
            <p role="status" className="m-0 mt-1 text-xs text-[var(--danger)]">優惠價沒有比原價低</p>
          ) : null}
          {editable && editing ? (
            <form
              className="mt-2 flex items-center gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                apply();
              }}
            >
              <input
                aria-label="一口價金額"
                inputMode="numeric"
                autoFocus
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                placeholder="含稅總價,例:100,000"
                className="tabular min-w-0 flex-1 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2 py-1 text-sm"
              />
              <button
                type="submit"
                aria-label="套用一口價"
                className="rounded-[var(--radius)] bg-[var(--accent)] px-2 py-1 text-xs font-medium text-[var(--on-accent)] hover:bg-[var(--accent-hover)]"
              >
                套用
              </button>
              <button
                type="button"
                aria-label="放棄修改一口價"
                onClick={() => setEditing(false)}
                className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)]"
              >
                <X aria-hidden="true" size={14} />
              </button>
            </form>
          ) : null}
          {editable && !editing ? (
            <div className="mt-2 flex justify-end gap-1.5">
              {special !== null ? (
                <button
                  type="button"
                  onClick={() => onSpecialPriceChange?.(null)}
                  className="rounded-[var(--radius)] px-2 py-1 text-xs text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                >
                  取消一口價
                </button>
              ) : null}
              <button
                type="button"
                onClick={startEditing}
                aria-label={special === null ? "一口價" : "修改一口價"}
                className="flex items-center gap-1 rounded-[var(--radius)] border border-[var(--border-strong)] px-2 py-1 text-xs text-[var(--text)] hover:bg-[var(--surface-2)]"
              >
                <Tag aria-hidden="true" size={12} />
                {special === null ? "一口價" : "修改"}
              </button>
            </div>
          ) : null}
          <p className="m-0 mt-1.5 text-xs text-[var(--text-3)]">金額單位為新台幣元;存檔時由後端重算為準。</p>
        </>
      )}
    </section>
  );
}
